import { type ReactNode } from 'react';
import { resizeImage } from '@hungryroot/image';
import {
  Carousel,
  CarouselTrack,
  CarouselSlide,
  CarouselProgress,
  IngredientsImageGrid,
  useCarouselControls,
} from '@hungryroot/pho';
import { type PairingDetail } from '@hungryroot/rest';
import { IconButton, CaretRight, CaretLeft } from '@hungryroot/ui-kit';

import { type PdpHeroAvailabilityBadgeVariant } from '../../types/pdpHeroAvailabilityBadge';
import { HeroImageArea } from './HeroImageArea';
import styles from '../RDP/RDP.module.css';

const MAX_SLIDES = 2;
const SLIDES_TO_SHOW = 1;

export interface RDPImageContentProps {
  imageUrl?: string;
  packagedImageUrl?: string | null;
  ingredients?: PairingDetail['ingredients'];
  shouldHideAIImage: boolean;
  onImageScroll?: (imageIndex?: number) => void;
  heroImageAvailabilityBadge?: PdpHeroAvailabilityBadgeVariant | null;
}

const ResponsiveImage = ({
  src,
  className,
}: {
  src: string;
  className?: string;
}) => (
  <picture>
    <source
      media="only screen and (max-width: 300px)"
      {...resizeImage({
        src,
        width: 300,
      })}
    />
    <source
      media="only screen and (min-width: 301px) and (max-width: 600px)"
      {...resizeImage({
        src,
        width: 600,
      })}
    />
    {/* on desktop, we only need 520 x 350, which matches the 600 width image */}
    <source
      media="only screen and (min-width: 1025px)"
      {...resizeImage({
        src,
        width: 600,
      })}
    />
    <img className={className} src={src} alt="" draggable={false} />
  </picture>
);

const PackagedImage = ({
  src,
  className,
}: {
  src: string;
  className?: string;
}) => (
  <picture>
    <source
      media="only screen and (max-width: 300px)"
      {...resizeImage({
        src,
        width: 300,
      })}
    />
    <source
      media="only screen and (min-width: 301px) and (max-width: 600px)"
      {...resizeImage({
        src,
        width: 600,
      })}
    />
    <img className={className} src={src} alt="" draggable={false} />
  </picture>
);

export function RDPImageContent({
  imageUrl,
  packagedImageUrl,
  ingredients,
  shouldHideAIImage,
  onImageScroll,
  heroImageAvailabilityBadge,
}: RDPImageContentProps): ReactNode {
  const carousel = useCarouselControls();

  const ingredientsGrid = ingredients ? (
    <div className={styles['image-container']}>
      <IngredientsImageGrid ingredients={ingredients} />
    </div>
  ) : null;

  const packagedImage = packagedImageUrl ? (
    <PackagedImage
      src={packagedImageUrl}
      className={styles['packaged-image']}
    />
  ) : null;

  if (shouldHideAIImage) {
    return (
      <HeroImageArea badge={heroImageAvailabilityBadge}>
        {ingredientsGrid ?? packagedImage}
      </HeroImageArea>
    );
  }

  if (imageUrl && packagedImageUrl) {
    const slides = [
      { src: imageUrl, isPackaged: false },
      { src: packagedImageUrl, isPackaged: true },
    ];

    // Navigation will trigger a re-render and update to the returned value so currentSlide is accurate
    const currentSlide = carousel.getFirstVisibleIndex();

    return (
      <HeroImageArea badge={heroImageAvailabilityBadge}>
        <Carousel
          carouselRef={carousel.ref}
          totalSlides={MAX_SLIDES}
          slidesToShow={SLIDES_TO_SHOW}
          onSlideChange={() => {
            carousel.onSlideChange();
            onImageScroll?.(currentSlide);
          }}
          ariaLabel="Product images"
        >
          {(carouselState) => (
            <>
              {!carousel.getIsBackDisabled() && (
                <IconButton
                  className={styles.back}
                  ariaLabel="Previous image"
                  icon={<CaretLeft />}
                  size="small"
                  style="muted"
                  prominence="primary"
                  onClick={() => {
                    carousel.back();
                    onImageScroll?.(currentSlide);
                  }}
                />
              )}
              {!carousel.getIsNextDisabled() && (
                <IconButton
                  className={styles.next}
                  ariaLabel="Next image"
                  icon={<CaretRight />}
                  size="small"
                  style="muted"
                  prominence="primary"
                  onClick={() => {
                    carousel.next();
                    onImageScroll?.(currentSlide);
                  }}
                />
              )}
              <CarouselTrack>
                {slides.map((slide, index) =>
                  slide.isPackaged ? (
                    <CarouselSlide
                      key={slide.src}
                      carousel={carouselState}
                      index={index}
                      className={styles.slide}
                    >
                      <PackagedImage
                        src={slide.src}
                        className={styles['packaged-image']}
                      />
                    </CarouselSlide>
                  ) : (
                    <CarouselSlide
                      key={slide.src}
                      carousel={carouselState}
                      index={index}
                      className={styles.slide}
                    >
                      <ResponsiveImage src={slide.src} />
                    </CarouselSlide>
                  )
                )}
              </CarouselTrack>
              <CarouselProgress
                carousel={carouselState}
                isVisualOnly={true}
                className={styles.progress}
              />
            </>
          )}
        </Carousel>
      </HeroImageArea>
    );
  }

  if (imageUrl) {
    return (
      <HeroImageArea badge={heroImageAvailabilityBadge}>
        <ResponsiveImage src={imageUrl} className={styles.image} />
      </HeroImageArea>
    );
  }

  return (
    <HeroImageArea badge={heroImageAvailabilityBadge}>
      {ingredientsGrid}
    </HeroImageArea>
  );
}
